<script setup lang="ts">
import { computed, reactive } from "vue"
import DashboardCard from "../components/DashboardCard.vue"
import DataState from "../components/DataState.vue"
import DashboardEmptyState from "../components/DashboardEmptyState.vue"
import EventTimeline from "../components/EventTimeline.vue"
import FilterBar from "../components/FilterBar.vue"
import FilterSearch from "../components/FilterSearch.vue"
import FilterSelect from "../components/FilterSelect.vue"
import MetricTile from "../components/MetricTile.vue"
import PageHeader from "../components/PageHeader.vue"
import DashboardLayout from "../layouts/DashboardLayout.vue"
import { useDashboardStore } from "../stores/dashboard"
import type { RecentEvent } from "../utils/dashboard-types"
import { formatNumber } from "../utils/format"

interface EnrichedEvent extends RecentEvent {
  nodeCity?: string | null
  nodeCountry?: string | null
  nodeIpAddress?: string | null
  sessionCountry?: string | null
}

type EventFilters = {
  component: string
  level: "all" | RecentEvent["level"]
  search: string
  type: string
}

const dashboard = useDashboardStore()
const defaultFilters: EventFilters = {
  component: "all",
  level: "all",
  search: "",
  type: "all"
}
const filters = reactive<EventFilters>({ ...defaultFilters })
const levelItems = [
  { label: "All levels", value: "all" },
  { label: "Info", value: "info" },
  { label: "Warn", value: "warn" },
  { label: "Error", value: "error" }
]
const componentItems = computed(() => [
  { label: "All components", value: "all" },
  ...Array.from(new Set(dashboard.events.map((event) => event.component)))
    .sort()
    .map((component) => ({ label: component, value: component }))
])
const typeItems = computed(() => [
  { label: "All event types", value: "all" },
  ...Array.from(new Set(dashboard.events.map((event) => event.type).filter((type): type is string => Boolean(type))))
    .sort()
    .map((type) => ({ label: type, value: type }))
])
const nodesById = computed(() => new Map(dashboard.nodes.map((node) => [node.id, node])))
const sessionsById = computed(() => new Map(dashboard.sessions.map((session) => [session.id, session])))
const enrichedEvents = computed<EnrichedEvent[]>(() =>
  dashboard.events.map((event) => {
    const node = event.nodeId ? nodesById.value.get(event.nodeId) : undefined
    const session = event.sessionId ? sessionsById.value.get(event.sessionId) : undefined

    return {
      ...event,
      nodeCity: node?.city ?? null,
      nodeCountry: node?.country ?? session?.requestedCountry ?? null,
      nodeIpAddress: node?.observedIpAddress ?? null,
      sessionCountry: session?.requestedCountry ?? null
    }
  })
)
const filteredEvents = computed(() => {
  const search = filters.search.trim().toLowerCase()

  return enrichedEvents.value.filter((event) => {
    const matchesSearch =
      !search ||
      [
        event.id,
        event.component,
        event.message,
        event.sessionId,
        event.nodeId,
        event.nodeIpAddress,
        event.nodeCountry,
        event.nodeCity,
        event.sessionCountry,
        event.clientId,
        event.type
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search)
    const matchesLevel = filters.level === "all" || event.level === filters.level
    const matchesComponent = filters.component === "all" || event.component === filters.component
    const matchesType = filters.type === "all" || event.type === filters.type

    return matchesSearch && matchesLevel && matchesComponent && matchesType
  })
})
const filtersDirty = computed(() =>
  Object.entries(defaultFilters).some(([key, value]) => filters[key as keyof EventFilters] !== value)
)
const hardEventCount = computed(() => dashboard.eventIngest.hardEvents)
const warnCount = computed(() => dashboard.events.filter((event) => event.level === "warn").length)
const nodeContextCount = computed(
  () => filteredEvents.value.filter((event) => Boolean(event.nodeId || event.nodeIpAddress || event.nodeCountry)).length
)
const ipCountryContextCount = computed(
  () => filteredEvents.value.filter((event) => Boolean(event.nodeIpAddress || event.nodeCountry)).length
)
const sessionContextCount = computed(() => filteredEvents.value.filter((event) => Boolean(event.sessionId)).length)
const clientContextCount = computed(() => filteredEvents.value.filter((event) => Boolean(event.clientId)).length)

function resetFilters() {
  Object.assign(filters, defaultFilters)
}
</script>

<template>
  <DashboardLayout>
    <div class="dashboard-events-page flex min-w-0 flex-col gap-5">
      <PageHeader
        title="Events"
        description="Accepted events with resolved context."
        details="Stored SDK, control plane, relay, and browser-client events with node, session, client, IP, and country context when available."
      />

      <DataState
        :loading="dashboard.isLoading"
        :error="dashboard.errorMessage"
        :empty="dashboard.events.length === 0"
        empty-title="No events"
        empty-description="No accepted regular events or hard events were returned by the Worker dashboard API."
      >
        <section class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <MetricTile
            label="Accepted batches"
            :value="formatNumber(dashboard.eventIngest.acceptedBatches)"
            tone="success"
            caption="Stored by control plane"
          />
          <MetricTile
            label="Duplicate batches"
            :value="
              dashboard.eventIngest.duplicateBatches === null
                ? 'n/a'
                : formatNumber(dashboard.eventIngest.duplicateBatches)
            "
            tone="neutral"
            caption="Replay-window duplicates"
          />
          <MetricTile
            label="Hard events"
            :value="formatNumber(hardEventCount)"
            tone="error"
            caption="Explicit hard-event endpoint records"
          />
          <MetricTile
            label="Warning events"
            :value="formatNumber(warnCount)"
            tone="warning"
            caption="Needs operator review"
          />
        </section>

        <section class="dashboard-events-content flex min-w-0 flex-1 flex-col gap-4">
          <FilterBar :count-label="formatNumber(filteredEvents.length)" :dirty="filtersDirty" @reset="resetFilters">
            <template #search>
              <FilterSearch
                v-model="filters.search"
                label="Search events"
                placeholder="Search event, session, node, client, IP, country"
                size="xs"
              />
            </template>
            <template #filters>
              <div class="grid gap-2 sm:grid-cols-3">
                <FilterSelect v-model="filters.level" :items="levelItems" label="Event level" size="xs" />
                <FilterSelect v-model="filters.component" :items="componentItems" label="Event component" size="xs" />
                <FilterSelect v-model="filters.type" :items="typeItems" label="Event type" size="xs" />
              </div>
            </template>
          </FilterBar>

          <section class="dashboard-event-columns grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <DashboardCard title="Event stream" static class="dashboard-event-stream-card">
              <template #actions>
                <span class="dashboard-mono text-sm text-(--dash-muted)">
                  {{ formatNumber(filteredEvents.length) }} shown
                </span>
              </template>
              <DashboardEmptyState
                v-if="filteredEvents.length === 0"
                title="No matching events"
                description="Relax the filters to show more retained events."
                variant="compact"
              />
              <template v-else>
                <UScrollArea class="dashboard-scroll-area dashboard-event-stream-scroll pr-2">
                  <EventTimeline :events="filteredEvents" :now="dashboard.summary.generatedAt" />
                </UScrollArea>
              </template>
            </DashboardCard>

            <DashboardCard title="Event context" static class="dashboard-event-context-card">
              <div class="grid gap-4">
                <div class="grid gap-3">
                  <div class="flex items-center justify-between gap-3 border-b border-(--dash-line) pb-3">
                    <span class="text-sm text-(--dash-muted)">Node context</span>
                    <span class="dashboard-mono text-sm text-(--dash-ink)">
                      {{ formatNumber(nodeContextCount) }} / {{ formatNumber(filteredEvents.length) }}
                    </span>
                  </div>
                  <div class="flex items-center justify-between gap-3 border-b border-(--dash-line) pb-3">
                    <span class="text-sm text-(--dash-muted)">IP and country</span>
                    <span class="dashboard-mono text-sm text-(--dash-ink)">
                      {{ formatNumber(ipCountryContextCount) }} / {{ formatNumber(filteredEvents.length) }}
                    </span>
                  </div>
                  <div class="flex items-center justify-between gap-3 border-b border-(--dash-line) pb-3">
                    <span class="text-sm text-(--dash-muted)">Session context</span>
                    <span class="dashboard-mono text-sm text-(--dash-ink)">
                      {{ formatNumber(sessionContextCount) }} / {{ formatNumber(filteredEvents.length) }}
                    </span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-sm text-(--dash-muted)">Client context</span>
                    <span class="dashboard-mono text-sm text-(--dash-ink)">
                      {{ formatNumber(clientContextCount) }} / {{ formatNumber(filteredEvents.length) }}
                    </span>
                  </div>
                </div>
              </div>
            </DashboardCard>
          </section>
        </section>
      </DataState>
    </div>
  </DashboardLayout>
</template>
